import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Mail, Loader2, AlertTriangle, KeyRound, ArrowLeft } from "lucide-react";
import { requestAdminOtp, verifyAdminOtp, setAdminSession, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CODE_LENGTH = 6;

export default function Admin2FA() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [phase, setPhase] = useState("idle"); // idle | requesting | awaiting_code | verifying
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
    const [cooldown, setCooldown] = useState(0);
    const [devCode, setDevCode] = useState("");
    const inputsRef = useRef([]);

    const from = location.state?.from || "/admin/overview";

    useEffect(() => {
        if (cooldown <= 0) return;
        const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(id);
    }, [cooldown]);

    const handleRequest = async () => {
        try {
            setPhase("requesting");
            setError("");
            setInfo("");
            setDevCode("");
            const res = await requestAdminOtp();
            setPhase("awaiting_code");
            setCooldown(60);
            const recipients = Array.isArray(res.sent_to) ? res.sent_to.join(", ") : res.sent_to;
            setInfo(`En kode er sendt til ${recipients}. Den udløber om 10 minutter.`);
            if (res.dev_code) {
                setDevCode(res.dev_code);
            }
            setTimeout(() => inputsRef.current[0]?.focus(), 100);
        } catch (e) {
            setPhase("idle");
            setError(formatApiError(e));
        }
    };

    const handleCodeChange = (idx, val) => {
        const v = val.replace(/\D/g, "").slice(0, 1);
        const next = [...code];
        next[idx] = v;
        setCode(next);
        if (v && idx < CODE_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
        if (v && idx === CODE_LENGTH - 1 && next.every((c) => c)) {
            handleVerify(next.join(""));
        }
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === "Backspace" && !code[idx] && idx > 0) {
            inputsRef.current[idx - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
        if (e.key === "ArrowRight" && idx < CODE_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
    };

    const handlePaste = (e) => {
        const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, CODE_LENGTH);
        if (!txt) return;
        e.preventDefault();
        const next = txt.split("").concat(Array(CODE_LENGTH - txt.length).fill(""));
        setCode(next);
        inputsRef.current[Math.min(txt.length, CODE_LENGTH - 1)]?.focus();
        if (txt.length === CODE_LENGTH) handleVerify(txt);
    };

    const handleVerify = async (codeStr) => {
        try {
            setPhase("verifying");
            setError("");
            const res = await verifyAdminOtp(codeStr);
            setAdminSession(res.admin_session_token);
            toast.success("2FA bekræftet — du er logget ind");
            navigate(from, { replace: true });
        } catch (e) {
            setPhase("awaiting_code");
            setError(formatApiError(e));
            setCode(Array(CODE_LENGTH).fill(""));
            setTimeout(() => inputsRef.current[0]?.focus(), 50);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex items-center justify-center p-4" data-testid="admin-2fa-page">
            <div className="w-full max-w-md">
                <button onClick={() => navigate("/dashboard")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="admin-2fa-back">
                    <ArrowLeft className="w-4 h-4" />Tilbage til dashboard
                </button>
                <div className="bg-white rounded-3xl border border-slate-200/70 shadow-xl p-8">
                    <div className="flex items-start gap-3 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 inline-flex items-center justify-center shrink-0">
                            <ShieldCheck className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">Bekræft din identitet</h1>
                            <p className="text-sm text-slate-500 mt-0.5">Super-admin adgang kræver 2FA via email</p>
                        </div>
                    </div>

                    {phase === "idle" && (
                        <div data-testid="admin-2fa-request-phase">
                            <p className="text-sm text-slate-600 leading-relaxed mb-5">
                                Når du klikker nedenfor, sender vi en 6-cifret kode til den verificerede admin-email. Koden er gyldig i 10 minutter.
                            </p>
                            <div className="bg-slate-50 rounded-xl p-3 mb-5 flex items-center gap-3">
                                <Mail className="w-4 h-4 text-slate-500 shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs text-slate-500">Koden sendes til</p>
                                    <p className="text-sm font-medium text-slate-900 truncate">Din verificerede admin-email</p>
                                </div>
                            </div>
                            {error && (
                                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-xs text-red-700">
                                    <AlertTriangle className="w-4 h-4 shrink-0" />{error}
                                </div>
                            )}
                            <button
                                onClick={handleRequest}
                                data-testid="admin-2fa-send-btn"
                                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-medium inline-flex items-center justify-center gap-2 transition"
                            >
                                <Mail className="w-4 h-4" />Send kode til min email
                            </button>
                            <p className="text-[11px] text-slate-400 mt-4 text-center">
                                Logget ind som <span className="font-medium">{user?.email}</span>
                            </p>
                        </div>
                    )}

                    {(phase === "awaiting_code" || phase === "verifying" || phase === "requesting") && (
                        <div data-testid="admin-2fa-verify-phase">
                            {info && (
                                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 inline-flex items-start gap-2">
                                    <Mail className="w-4 h-4 shrink-0" />
                                    <span>{info}</span>
                                </div>
                            )}
                            {devCode && (
                                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="admin-2fa-dev-code">
                                    <p className="font-semibold mb-1">🛠 Dev-tilstand (Resend ikke konfigureret endnu)</p>
                                    <p>Din kode er <span className="font-mono font-bold text-base tracking-widest">{devCode}</span> — koden er kun synlig her indtil du sætter <code>RESEND_API_KEY</code> i <code>.env</code>.</p>
                                </div>
                            )}
                            <label className="block text-sm font-medium text-slate-700 mb-3">Indtast 6-cifret kode</label>
                            <div className="flex gap-2 justify-center mb-5" data-testid="admin-2fa-code-inputs">
                                {code.map((c, i) => (
                                    <input
                                        key={i}
                                        ref={(el) => (inputsRef.current[i] = el)}
                                        value={c}
                                        onChange={(e) => handleCodeChange(i, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(i, e)}
                                        onPaste={i === 0 ? handlePaste : undefined}
                                        disabled={phase === "verifying"}
                                        inputMode="numeric"
                                        maxLength={1}
                                        data-testid={`admin-2fa-input-${i}`}
                                        className="w-12 h-14 text-center text-2xl font-bold border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition bg-white"
                                    />
                                ))}
                            </div>
                            {error && (
                                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-xs text-red-700" data-testid="admin-2fa-error">
                                    <AlertTriangle className="w-4 h-4 shrink-0" />{error}
                                </div>
                            )}
                            <button
                                onClick={() => handleVerify(code.join(""))}
                                disabled={code.some((c) => !c) || phase === "verifying"}
                                data-testid="admin-2fa-verify-btn"
                                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium inline-flex items-center justify-center gap-2 transition"
                            >
                                {phase === "verifying" ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                                {phase === "verifying" ? "Verificerer…" : "Bekræft kode"}
                            </button>
                            <div className="flex items-center justify-between mt-4 text-xs">
                                <button
                                    onClick={handleRequest}
                                    disabled={cooldown > 0 || phase === "requesting"}
                                    data-testid="admin-2fa-resend"
                                    className="text-blue-600 hover:text-blue-700 disabled:text-slate-400 disabled:cursor-not-allowed font-medium"
                                >
                                    {cooldown > 0 ? `Send ny kode om ${cooldown} sek.` : "Send en ny kode"}
                                </button>
                                <span className="text-slate-400">Max 5 forsøg</span>
                            </div>
                        </div>
                    )}
                </div>
                <p className="text-[11px] text-slate-400 text-center mt-4">
                    🔒 Beskyttet område · 2FA via email-OTP · 24 timers session
                </p>
            </div>
        </div>
    );
}
